import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { ProductRow } from '../../../lib/supabase';
import { products as mockProducts, categories } from '../../../mocks/products';

interface Props {
  selectedCat: string;
  selectedSub: string;
}

interface DetailModalProps {
  product: ProductRow;
  onClose: () => void;
}

function DetailModal({ product, onClose }: DetailModalProps) {
  const cat = categories.find(c => c.id === product.category_id);
  const sub = cat?.subCategories.find(s => s.id === product.sub_category_id);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 bg-[#1a2535]">
          <h3 className="text-white font-bold text-base leading-snug">{product.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white cursor-pointer w-8 h-8 flex items-center justify-center">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-6 p-6">
          <div className="w-full sm:w-44 h-44 flex-shrink-0 border border-gray-100 flex items-center justify-center p-3">
            <img src={product.image} alt={product.title} className="w-full h-full object-contain" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-400">型號：</span>
              <span className="text-sm font-mono font-semibold text-[#1a73b5]">{product.model}</span>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-400">分類：</span>
              <span className="text-xs text-gray-600">{cat?.label}</span>
              {sub && <><span className="text-gray-300">›</span><span className="text-xs text-gray-600">{sub.label}</span></>}
            </div>
            <p className="text-sm text-gray-600 leading-[1.9] mb-4">{product.description}</p>
            <div className="flex flex-wrap gap-1.5">
              {product.tags.map(tag => (
                <span key={tag} className="text-xs bg-[#f0f6ff] text-[#1a73b5] border border-[#d0e8f8] px-2 py-0.5">{tag}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-100 pt-4">
          <a href="/contact" className="inline-flex items-center gap-1.5 bg-[#1a73b5] text-white text-sm px-5 py-2 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-mail-send-line" />詢問報價
          </a>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-600 text-sm px-5 py-2 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductGrid({ selectedCat, selectedSub }: Props) {
  const [activeProduct, setActiveProduct] = useState<ProductRow | null>(null);
  const [allProducts, setAllProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error || !data || data.length === 0) {
        // Fallback to mock data
        const mapped: ProductRow[] = mockProducts.map(p => ({
          id: p.id,
          category_id: p.categoryId,
          sub_category_id: p.subCategoryId,
          title: p.title,
          model: p.model,
          image: p.image,
          tags: p.tags,
          description: p.desc,
          is_active: true,
          sort_order: 0,
          created_at: '',
          updated_at: '',
        }));
        setAllProducts(mapped);
      } else {
        setAllProducts(data);
      }
      setLoading(false);
    };
    fetchProducts();
  }, []);

  const filtered = allProducts.filter(p => {
    if (selectedCat === 'all') return true;
    if (selectedSub === 'all') return p.category_id === selectedCat;
    return p.category_id === selectedCat && p.sub_category_id === selectedSub;
  });

  const currentCat = categories.find(c => c.id === selectedCat);
  const currentSub = currentCat?.subCategories.find(s => s.id === selectedSub);
  const titleLabel = selectedCat === 'all' ? '全部產品' : selectedSub === 'all' ? currentCat?.label ?? '產品列表' : currentSub?.label ?? '產品列表';

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <i className="ri-loader-4-line text-3xl text-gray-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-1 h-5 bg-[#1a73b5] flex-shrink-0" />
          <h2 className="text-base font-bold text-gray-800">{titleLabel}</h2>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">共 {filtered.length} 項產品</span>
      </div>

      {filtered.length === 0 ? (
        <div className="py-20 text-center text-gray-400">
          <i className="ri-inbox-line text-5xl mb-3 block" />
          <p className="text-sm">此分類目前尚無產品</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(product => (
            <div
              key={product.id}
              className="group border border-gray-100 hover:border-[#1a73b5] transition-all duration-200 cursor-pointer overflow-hidden flex flex-col"
              onClick={() => setActiveProduct(product)}
            >
              <div className="w-full h-40 bg-white flex items-center justify-center p-3 overflow-hidden">
                <img src={product.image} alt={product.title} title={product.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="px-3 py-3 border-t border-gray-100 flex flex-col flex-1">
                <p className="text-xs font-mono text-[#1a73b5] mb-1">{product.model}</p>
                <h3 className="text-sm text-gray-700 leading-snug line-clamp-2">{product.title}</h3>
                <div className="flex flex-wrap gap-1 mt-2 min-h-[1.25rem]">
                  {product.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="text-[10px] bg-[#f0f6ff] text-[#1a73b5] px-1.5 py-0.5">{tag}</span>
                  ))}
                </div>
                <button className="mt-auto pt-3 w-full text-center text-xs bg-[#1a73b5] text-white py-1.5 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap">
                  查看詳情
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeProduct && <DetailModal product={activeProduct} onClose={() => setActiveProduct(null)} />}
    </div>
  );
}
